import { InputAdornment } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Button, Col, Placeholder, Row } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { User } from '../../../model/user/User';
import { Pagination } from '../../../shared/components/Pagination';
import { useAgencyCreators } from '../../../shared/hooks/useAgencyCreators';
import { setSelectetInfluencer } from '../../../store/agency/agencySlice';
import { IAllStates } from '../../../store/rootReducer';
import EmptyState from '../../../shared/components/placeholder/EmptyState';
import { useTranslation } from 'react-i18next';
import { truncateString } from '../../../shared/functions/Functions';
import TooltipOverlay from '../../../shared/components/tooltip/Tooltip';
import { CustomImageHandler } from '../../../shared/components/CustomImageHandler';
import { SearchInput } from '../../../shared/components/SearchInput';

const SKELETON_COUNT = 4;
const LOAD_MORE_SKELETON_COUNT = 4;

function AgencyCreatorItemSkeleton() {
    return (
        <Col xs={6} lg={3} className="mb-3 agency-creator-item placeholder-glow" aria-hidden="true">
            <div className="d-flex flex-column align-items-center">
                <Placeholder className="skeleton skeleton-user-avatar-xl" />
                <Placeholder className="skeleton skeleton-table-name d-block mt-3" />
            </div>
        </Col>
    );
}

interface IFindAgencyCreatorsProps {
    closeModal?: () => void;
    renderPagination?: boolean;
    idScroll?: string;
}

export function FindAgencyCreators({ closeModal, renderPagination = false, idScroll }: IFindAgencyCreatorsProps) {
    const dispatch = useDispatch();
    const { selectedInfluencer } = useSelector((state: IAllStates) => state.agency);
    const { users, totalPages, filterParams, count, setFilterParams, isLoading, isFetchingMore } = useAgencyCreators();
    const { t } = useTranslation();
    const [usersCreators, setUsersCreators] = useState<User[]>([]);
    const isMobile = window.innerWidth <= 700;
    const scrollId = idScroll ?? 'scrollableAgencyCreators';

    useEffect(() => {
        setUsersCreators(prev => (filterParams.page === 1 || renderPagination ? users : [...prev, ...users]));
    }, [users]);

    const deselectInfluencer = () => {
        dispatch(setSelectetInfluencer(undefined));
        closeModal?.();
    };

    const selectInfluencer = (user: Partial<User>) => {
        if (selectedInfluencer?.id !== user.id) {
            dispatch(setSelectetInfluencer(user));
            closeModal?.();
        }
    };

    const handleSearch = (value: string) => {
        setFilterParams(prevState => ({ ...prevState, displayName: value, page: 1 }));
    };

    const showSkeleton = isLoading || (renderPagination && isFetchingMore);

    return (
        <>
            <Row className="justify-content-center">
                <Col onClick={deselectInfluencer} className="mb-3 cursor-pointer d-md-none text-center">
                    <Button className="btn-outline-primary w-sm-50 w-100">
                        <span>Select all creators</span>
                        <img
                            src={'/icons/img-all-users.png'}
                            className="ml-2"
                            width="20"
                            height="20"
                            alt="User photo"
                            onError={(e: any) => {
                                e.target.onerror = null;
                                e.target.src = '/icons/UserPlaceholder.svg';
                            }}
                        />
                    </Button>
                </Col>
                <Col md={6} className="mb-3">
                    <SearchInput
                        name="displayName"
                        defaultValue={filterParams?.displayName ?? ''}
                        onSearch={handleSearch}
                        isLoading={isLoading}
                        label="Content creator search"
                        className="w-sm-50 w-100"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" className="pr-2">
                                    <FaSearch className="text-muted cursor-pointer" size={16} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Col>
            </Row>

            {showSkeleton ? (
                <div className="w-100 holder-row px-3">
                    <div className="row agency-creator-row">
                        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                            <AgencyCreatorItemSkeleton key={i} />
                        ))}
                    </div>
                </div>
            ) : usersCreators?.length ? (
                <div id={scrollId} className="w-100 overflow-auto holder-row px-3">
                    <InfiniteScroll
                        scrollableTarget={scrollId}
                        dataLength={usersCreators?.length || 0}
                        className="row agency-creator-row"
                        next={() => {
                            setFilterParams(prev => ({ ...prev, page: prev.page + 1 }));
                        }}
                        endMessage={
                            !renderPagination && (
                                <div className="col-12">
                                    <p className="text-center">
                                        <b>{t('agency.seen_all_users')}</b>
                                    </p>
                                </div>
                            )
                        }
                        hasMore={renderPagination ? false : (usersCreators?.length || 0) < Number(count)}
                        loader={
                            !renderPagination && (
                                <>
                                    {Array.from({ length: LOAD_MORE_SKELETON_COUNT }).map((_, i) => (
                                        <AgencyCreatorItemSkeleton key={i} />
                                    ))}
                                </>
                            )
                        }
                    >
                        {usersCreators.map(item => {
                            const isSelected = item.id === selectedInfluencer?.id;
                            const name = item.displayName || `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim();
                            const maxLength = isMobile ? 15 : 20;
                            const truncatedName = truncateString(name, maxLength);
                            const isTruncated = name.length > maxLength;

                            return (
                                <Col
                                    onClick={() => selectInfluencer(item)}
                                    xs={6}
                                    lg={3}
                                    className="mb-3 cursor-pointer agency-creator-item"
                                    key={item.id}
                                >
                                    <div
                                        className={`d-flex flex-column align-items-center ${
                                            isSelected ? 'selected-user' : ''
                                        }`}
                                    >
                                        <div className="position-relative">
                                            <CustomImageHandler
                                                thumbnailPath={item?.profilePhoto?.thumbnail}
                                                photoPath={item?.profilePhoto?.path}
                                                altTag="User Profile"
                                                classes={`agency-creator-avatar ${
                                                    isSelected ? 'selected-agency-photo' : ''
                                                } rounded-circle img-cover shadow user-img-rounded`}
                                                name={name}
                                            />
                                            {isSelected && (
                                                <p
                                                    className="mb-0 active-badge"
                                                    style={{ bottom: -8, left: 'calc(50% - 27.5px)' }}
                                                >
                                                    Active
                                                </p>
                                            )}
                                        </div>
                                        <TooltipOverlay content={isTruncated ? name : ''}>
                                            <h6 className="mb-0 mt-3">{truncatedName}</h6>
                                        </TooltipOverlay>
                                    </div>
                                </Col>
                            );
                        })}
                    </InfiniteScroll>
                    {renderPagination && totalPages > 1 && (
                        <div className="row mt-3">
                            <div className="col-12 d-flex justify-content-end">
                                <Pagination
                                    page={filterParams?.page}
                                    totalPages={totalPages}
                                    handlePagination={newPage => {
                                        setFilterParams(prev => ({ ...prev, page: newPage }));
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <EmptyState desc={t('agency.user_empty')} btnText={t('agency.add_more_creators')} />
            )}
        </>
    );
}
